import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Printer, Share2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api, { getErrorMessage } from '@/services/api';
import { API_ENDPOINTS } from '@/utils/constants';
import { Receipt, ApiResponse } from '@/services/types';
import { Sheet, Button, LoadingSkeleton, Alert } from '@/components/ui';
import { formatCurrency } from '@/utils/formatters';

interface ReceiptViewerProps {
    receiptNumber: string;
    shopId: number;
    isOpen: boolean;
    onClose: () => void;
}

const Row: React.FC<{ l: React.ReactNode; r: React.ReactNode; bold?: boolean; muted?: boolean; big?: boolean }> = ({ l, r, bold, muted, big }) => (
    <div className={`flex justify-between gap-3 ${muted ? 'text-ink-muted' : ''} ${bold ? 'font-bold' : ''} ${big ? 'mt-1 text-base font-black' : ''}`}>
        <span className="min-w-0 truncate">{l}</span><span className="shrink-0">{r}</span>
    </div>
);
const Dash = () => <div className="my-1 border-t border-dashed border-line" />;

/** 09 Struk — receipt paper built from the structured receipt; browser print via the server HTML. */
export const ReceiptViewer: React.FC<ReceiptViewerProps> = ({ receiptNumber, shopId, isOpen, onClose }) => {
    const { t } = useTranslation();
    const [receipt, setReceipt] = useState<Receipt | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen && receiptNumber) fetchReceipt();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, receiptNumber]);

    const fetchReceipt = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.get<ApiResponse<Receipt>>(API_ENDPOINTS.RECEIPT(shopId, receiptNumber));
            setReceipt(response.data.data);
        } catch (err) {
            const message = getErrorMessage(err);
            setError(message);
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = () => {
        if (!receipt?.htmlReceipt) return;
        const printWindow = window.open('', '_blank');
        if (printWindow) {
            printWindow.document.write(receipt.htmlReceipt);
            printWindow.document.close();
            printWindow.focus();
            printWindow.print();
        }
    };

    const handleDownloadText = () => {
        if (!receipt?.textReceipt) return;
        const blob = new Blob([receipt.textReceipt], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `receipt-${receipt.receiptNumber}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success(t('transactions.receiptDownloaded'));
    };

    const share = async () => {
        if (!receipt) return;
        const text = receipt.textReceipt || `${receipt.receiptNumber} · ${formatCurrency(receipt.grandTotal)}`;
        if (navigator.share) { try { await navigator.share({ title: receipt.receiptNumber, text }); } catch { /* cancelled */ } }
        else handleDownloadText();
    };

    const when = receipt ? new Date(receipt.transactionTime) : null;
    const whenLabel = when && !isNaN(when.getTime()) ? when.toLocaleString('id-ID', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : receipt?.transactionTime;

    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            title={t('transactions.receipt')}
            size="md"
            footer={receipt && (
                <div className="flex flex-col gap-2.5">
                    <div className="grid grid-cols-2 gap-2.5">
                        <Button variant="outline" fullWidth icon={<Share2 />} onClick={share}>{t('common.share')}</Button>
                        <Button variant="outline" fullWidth icon={<Download />} onClick={handleDownloadText}>{t('transactions.downloadText')}</Button>
                    </div>
                    <Button variant="secondary" size="lg" fullWidth icon={<Printer />} onClick={handlePrint} disabled={!receipt.htmlReceipt}>{t('transactions.print')}</Button>
                </div>
            )}
        >
            {loading && <LoadingSkeleton height={400} />}
            {error && <Alert variant="error" message={error} />}
            {!loading && receipt && (
                <div className="mx-auto flex max-w-[400px] flex-col gap-[5px] rounded-[6px] bg-white p-5 text-[13px] text-neutral-800 shadow-md tnum">
                    <span className="self-center text-xl font-black tracking-[-0.02em] text-ink-brand">runut</span>
                    <span className="mt-1 text-center text-sm font-extrabold">{receipt.shopName}</span>
                    {receipt.shopAddress && <span className="text-center text-xs text-ink-muted">{receipt.shopAddress}</span>}
                    <Dash />
                    <Row l={receipt.receiptNumber} r={whenLabel} />
                    <Row l={`${t('transactions.cashier')}: ${receipt.cashierName}`} r={receipt.customerName || receipt.transactionType} muted />
                    <Dash />
                    {receipt.items?.length ? receipt.items.map((it, i) => (
                        <Row key={i} l={`${it.quantity}× ${it.name}`} r={formatCurrency(it.total).replace(/^Rp\s?/, '')} bold />
                    )) : <pre className="whitespace-pre-wrap font-mono text-xs">{receipt.textReceipt}</pre>}
                    <Dash />
                    <Row l={t('cart.subtotal')} r={formatCurrency(receipt.subtotal).replace(/^Rp\s?/, '')} />
                    {receipt.discountTotal > 0 && <Row l={t('cart.discount')} r={`−${formatCurrency(receipt.discountTotal).replace(/^Rp\s?/, '')}`} />}
                    <Row l={t('cart.tax')} r={formatCurrency(receipt.taxTotal).replace(/^Rp\s?/, '')} />
                    {receipt.serviceChargeTotal > 0 && <Row l={t('cart.serviceCharge')} r={formatCurrency(receipt.serviceChargeTotal).replace(/^Rp\s?/, '')} />}
                    <Row l="TOTAL" r={formatCurrency(receipt.grandTotal).replace(/^Rp\s?/, '')} big />
                    <Row l={receipt.invoice?.bankCode || t('checkout.paymentAmount')} r={formatCurrency(receipt.paymentAmount).replace(/^Rp\s?/, '')} />
                    {receipt.changeAmount > 0 && <Row l={t('checkout.change')} r={formatCurrency(receipt.changeAmount).replace(/^Rp\s?/, '')} bold />}
                    <Dash />
                    <span className="text-center text-xs text-ink-secondary">{t('receipt.thanks')}</span>
                    <span className="text-center font-extrabold text-ink-brand">{receipt.footerText || t('app.tagline')}</span>
                </div>
            )}
        </Sheet>
    );
};

export default ReceiptViewer;
